import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tours } from '../../../mocks/tours';

export default function FeaturedTours() {
  const { t } = useTranslation();
  const featured = tours.slice(0, 3);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-amber-500 font-semibold text-sm uppercase tracking-widest mb-3">
            {t('featured.badge')}
          </p>
          <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
            {t('featured.title')}
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            {t('featured.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {featured.map((tour, idx) => (
            <div
              key={tour.id}
              className={`group rounded-xl overflow-hidden bg-white border border-gray-100 hover:border-emerald-200 transition-all duration-500 cursor-pointer ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              <div className="relative w-full h-56 overflow-hidden">
                <img
                  src={tour.image}
                  alt={tour.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                {tour.tag && (
                  <span className="absolute top-4 left-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {tour.tag}
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-emerald-700 bg-emerald-50 text-xs font-medium px-2.5 py-1 rounded-full">
                    {tour.category}
                  </span>
                  <span className="text-gray-400 text-xs">{tour.duration}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2 leading-snug font-playfair">
                  {tour.title}
                </h3>
                <div className="flex items-center gap-1 mb-3">
                  <div className="w-4 h-4 flex items-center justify-center text-amber-400">
                    <i className="ri-star-fill text-sm"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">{tour.rating}</span>
                  <span className="text-gray-400 text-xs">({tour.reviews} {t('featured.reviews')})</span>
                </div>

                <ul className="mb-4 space-y-1">
                  {tour.highlights.slice(0, 3).map((h) => (
                    <li key={h} className="flex items-center gap-2 text-gray-500 text-xs">
                      <div className="w-3 h-3 flex items-center justify-center text-emerald-500 flex-shrink-0">
                        <i className="ri-check-line text-xs"></i>
                      </div>
                      {h}
                    </li>
                  ))}
                </ul>

                <div className="flex items-end justify-between mt-4 pt-4 border-t border-gray-100">
                  <div>
                    {tour.originalPrice && (
                      <p className="text-gray-400 text-xs line-through">
                        RM {tour.originalPrice.toLocaleString()}
                      </p>
                    )}
                    <p className="text-emerald-700 font-bold text-xl">
                      RM {tour.price.toLocaleString()}
                      <span className="text-gray-400 text-xs font-normal ml-1">{t('featured.perPax')}</span>
                    </p>
                  </div>
                  <Link
                    to="/tours"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('featured.viewDetails')}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className={`text-center mt-10 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <Link
            to="/tours"
            className="inline-flex items-center gap-2 border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white font-semibold px-7 py-3 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('featured.viewAll')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
